import React, { useState, useEffect } from "react";
import Paginator from "../../shared/Paginator";
import API from "../../services/api";
import { alphaformatDateTime } from "../../utils/dateFormat";
import { Toast } from "../../shared/Toast";
const ExchangeRates = () => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newRate, setNewRate] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  const loadData = async (page = 0, monthFilter = null) => {
    setLoading(true);
    try {
      const response = await API({
        url: "/adpartners/getExchangeRates",
        method: "POST",
        data: { page, limit: 10, month: monthFilter },
      });
      setRates(response.data.data || []);
      setTotalPages(Math.ceil(+response.data.total / 10) || 0);
    } catch (err) {
      console.error("Failed to load rates", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(currentPage, selectedMonth);
  }, [currentPage, selectedMonth]);

  const handleAddRate = async () => {
    if (!selectedMonth || !newRate) return;

    setSaving(true);
    try {
      const result = await API({
        url: "/adpartners/addExchangeRate",
        method: "POST",
        data: { rate: parseFloat(newRate), month: selectedMonth },
      });
      if(result.status==='SUCCESS'){
          Toast("success","Exchange Rate Added Successfully");
      }else Toast("error","Failed To Add Exchange Rate")

      setShowAddModal(false);
      setNewRate("");
      setSelectedMonth(null)
      loadData(currentPage, selectedMonth);
    } catch (err) {
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (id) => {
    if (!editValue || isNaN(editValue)) return;

    try {
      const result = await API({
        url: "/adpartners/editExchangeRate",
        method: "POST",
        data: { id, rate: parseFloat(editValue) },
      });
      if(result.status==='SUCCESS'){
          Toast("success","Exchange Rate Edited Successfully");
      }else Toast("error","Failed To Edit Exchange Rate")
      setEditingId(null);
      loadData(currentPage, selectedMonth);
    } catch (err) {
      alert("Failed to update");
    }
  };

  const SkeletonRow = () => (
    <tr className="animate-pulse">
      <td className="px-6 py-4">
        <div className="h-4 bg-gray-200 rounded w-20"></div>
      </td>
      <td className="px-6 py-4">
        <div className="h-4 bg-gray-200 rounded w-32"></div>
      </td>
      <td className="px-6 py-4">
        <div className="h-4 bg-gray-200 rounded w-24"></div>
      </td>
      <td className="px-6 py-4">
        <div className="h-8 bg-gray-200 rounded w-20"></div>
      </td>
    </tr>
  );

  return (
    <div className="p-6 w-full mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Exchange Rates</h1>

        <button onClick={() => setShowAddModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-5 rounded-lg shadow-md transition flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Exchange Rate
        </button>
      </div>

      <div className="mb-4">
        <input
          type="month"
          value={selectedMonth}
          onChange={(e) => {
            setSelectedMonth(e.target.value);
            setCurrentPage(0);
          }}
          className="px-4 py-2 border rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {selectedMonth && (
          <button
            onClick={() => {
              setSelectedMonth("");
              setCurrentPage(0);
            }}
            className="ml-3 text-sm text-red-600 hover:text-red-800"
          >
            Clear filter
          </button>
        )}
      </div>

      <div className="bg-white shadow overflow-hidden rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            ) : rates.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                  No exchange rates found
                </td>
              </tr>
            ) : (
              rates.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.month}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {editingId === item.id ? (
                      <input type="number" step="0.0001" defaultValue={item.price} onChange={(e) => setEditValue(e.target.value)} className="w-24 px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500" autoFocus />
                    ) : (
                      <span className="font-medium">{item.price}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {editingId === item.id ? (
                      <div className="flex gap-2">
                        <button onClick={() => handleEdit(item.id)} className="text-green-600 hover:text-green-800 font-medium">
                          Save
                        </button>
                        <button onClick={() => setEditingId(null)} className="text-gray-600 hover:text-gray-800">
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingId(item.id);
                          setEditValue(item.price);
                        }}
                        className="text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 0 && (
        <div className="mt-6 flex justify-end">
          <Paginator pageCount={totalPages} forcePage={currentPage} handlePageClick={(e)=>setCurrentPage(e.selected)} />
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
            <h2 className="text-xl font-bold mb-4">Add New Exchange Rate</h2>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
              <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Exchange Rate</label>
              <input type="number" step="0.0001" value={newRate} onChange={(e) => setNewRate(e.target.value)} placeholder="e.g. 1.2345" className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewRate("");
                }}
                className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
                disabled={saving}
              >
                Cancel
              </button>
              <button onClick={handleAddRate} disabled={!selectedMonth || !newRate || saving} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {saving ? "Saving..." : "Add Rate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExchangeRates;
